import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesAPI } from '../services/api';
import { useCrudOperations } from '../hooks/useCrudOperations';
import '../styles/CompanyManagement.css';

const CompanyManagement = () => {
  const navigate = useNavigate();
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
  });

  const {
    data: companies,
    loading,
    error,
    successMessage,
    loadData,
    create,
    remove
  } = useCrudOperations({
    apiService: companiesAPI,
    resourceName: 'company'
  });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      // Auto-generate slug from name if name is being changed
      ...(name === 'name' && {
        slug: value
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, ''),
      }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await create(
      { name: formData.name, slug: formData.slug },
      `Created company "${formData.name}" successfully`
    );

    if (result) {
      setFormData({ name: '', slug: '' });
      setShowCompanyForm(false);
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    await remove(
      companyId,
      `Are you sure you want to DELETE "${companyName}"? This will permanently delete all associated dashboards, widgets, metrics, and API keys. This action CANNOT be undone!`,
      'Company and all associated data deleted successfully'
    );
  };

  if (loading) {
    return (
      <div className="company-management-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-management-container">
      <div className="company-management-header">
        <h2>Company Management</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button onClick={() => setShowCompanyForm(!showCompanyForm)} className="btn-primary">
            {showCompanyForm ? 'Cancel' : '+ Create New Company'}
          </button>
          <button onClick={() => navigate('/settings')} className="btn-secondary">
            Back to Settings
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showCompanyForm && (
        <div className="company-form-section">
          <h3>Create New Company</h3>
          <form onSubmit={handleSubmit} className="company-form">
            <div className="form-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={2}
                  placeholder="Kinspeed Ltd"
                />
              </div>
              <div className="form-group">
                <label>Slug * (URL-friendly identifier)</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  minLength={2}
                  pattern="[a-z0-9-]+"
                  placeholder="kinspeed"
                />
                <small>Lowercase letters, numbers, and hyphens only</small>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowCompanyForm(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Create Company
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="companies-section">
        <h3>All Companies ({companies.length})</h3>
        <div className="companies-grid">
          {companies.map((company) => (
            <div key={company.id} className="company-card">
              <div className="company-card-header">
                <h4>{company.name}</h4>
              </div>
              <p className="company-slug">/{company.slug}</p>
              <div className="company-card-actions">
                <button
                  onClick={() => handleDeleteCompany(company.id, company.name)}
                  className="btn-danger-small"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;
